import PropTypes from 'prop-types';
import React, { createContext, useContext, useState } from 'react';
import { shortenFullTimestamp } from '@general/dates';

const getPastDateFilter = () => {
    const now = new Date();
    const daysAgo = now.setDate(now.getDate() - 8);
    return `created_at_timestamp >= ${shortenFullTimestamp(daysAgo)}`;
};

const getDefaultEventFilter = showPastItems => {
    const defaultFilter = `NOT event_types:"Committee Events"`;
    if (showPastItems) {
        return defaultFilter;
    }
    return `${defaultFilter} AND ${getPastDateFilter()}`;
};

const getDefaultConfigFilter = ({ eventCode, companyId, isPublicationsSearch, showPastItems, searchType }) => {
    // publication search
    if (isPublicationsSearch) {
        return `content_type:publication`;
    }

    // Attendee search: filter by event code on events_attended.code - display inactive members
    if (eventCode && searchType.index === 'member_events') {
        return `event.code:${eventCode}`;
    }

    if (eventCode && searchType.index === 'companies') {
        return `events_attended:${eventCode}`;
    }

    // Member search for company: filter company member search by company id AND Active
    if (companyId) {
        return `company_id:${companyId} AND NOT status:Inactive`;
    }

    // Everything, Member & Company search: filter inactive members and companies and committee events
    if (searchType.index === 'everything') {
        return `NOT status:Inactive AND NOT event_types:"Committee Events"`;
    }

    if (searchType.index === 'members' || searchType.index === 'companies') {
        return `NOT status:Inactive`;
    }

    if (searchType.index === 'properties') {
        return '';
    }

    // defaults to event
    return getDefaultEventFilter(showPastItems);
};

export const SearchFiltersContext = createContext();
export const useSearchFiltersContext = () => {
    return useContext(SearchFiltersContext);
};

export const SearchFiltersProvider = ({
    children,
    eventCode: paramEventCode,
    companyId,
    isPublicationsSearch,
    initialShowPastItems = true,
    searchType,
}) => {
    const [configFilter, setConfigFilter] = useState(
        getDefaultConfigFilter({
            eventCode: paramEventCode,
            companyId,
            isPublicationsSearch,
            showPastItems: initialShowPastItems,
            searchType,
        })
    );

    const [includePastEvents, setIncludePastEvents] = useState(initialShowPastItems);
    const [dateRangeFilter, setDateRangeFilter] = useState('');

    const setConfigFilterForSearchType = ({ eventCode, newSearchType }) => {
        setIncludePastEvents(newSearchType.showPastItems);
        setConfigFilter(
            getDefaultConfigFilter({
                eventCode,
                showPastItems: newSearchType.showPastItems,
                searchType: newSearchType,
            })
        );
    };

    const toggleToIncludePastDates = toInclude => {
        setIncludePastEvents(toInclude);

        const now = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(now.getMonth() - 6);

        const newPastEventFilter = toInclude
            ? `meeting_end_timestamp >= ${shortenFullTimestamp(sixMonthsAgo.getTime())}`
            : getPastDateFilter();

        const filters = ['NOT event_types:"Committee Events"'];

        if (newPastEventFilter) {
            filters.push(newPastEventFilter);
        }

        if (dateRangeFilter) {
            filters.push(dateRangeFilter);
        }

        setConfigFilter(filters.join(' AND '));
    };

    // all event dates are in Eastern, startDate, endDate already converted
    const updateDateRangeFilter = (startDate, endDate) => {
        let newDateRangeFilter = '';

        if (startDate && !endDate) {
            const start = shortenFullTimestamp(new Date(startDate).getTime());
            newDateRangeFilter = `created_at_timestamp >= ${start} `;
        }
        if (!startDate && endDate) {
            const end = shortenFullTimestamp(new Date(endDate).getTime());
            newDateRangeFilter = `created_at_timestamp <= ${end} `;
        }
        if (startDate && endDate) {
            const start = shortenFullTimestamp(new Date(startDate).getTime());
            const end = shortenFullTimestamp(new Date(endDate).getTime());
            newDateRangeFilter = `created_at_timestamp: ${start} TO ${end} OR meeting_end_timestamp: ${start} TO ${end}`;
        }

        const filters = ['NOT event_types:"Committee Events"'];
        if (!includePastEvents) {
            filters.push(getPastDateFilter());
        }

        if (newDateRangeFilter) {
            filters.push(newDateRangeFilter);
        }
        setDateRangeFilter(newDateRangeFilter);
        setConfigFilter(filters.join(' AND '));
    };

    return (
        <SearchFiltersContext.Provider
            value={{
                configFilter,
                setConfigFilterForSearchType,
                toggleToIncludePastDates,
                updateDateRangeFilter,
                includePastEvents,
            }}
        >
            {children}
        </SearchFiltersContext.Provider>
    );
};

SearchFiltersProvider.propTypes = {
    children: PropTypes.node.isRequired,
    eventCode: PropTypes.string,
    companyId: PropTypes.string,
    isPublicationsSearch: PropTypes.bool,
    initialShowPastItems: PropTypes.bool,
    searchType: PropTypes.object,
};
